import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { useSocket } from '../../context/SocketContext';
import SellerDashboardLayout from '../../components/SellerDashboardLayout';
import { API_BASE_URL } from '../../config/api';
import * as messageService from '../../services/messageService';
import { useCurrency } from '../../context/CurrencyContext';
import './MessagePage.css';

const MessagePage = () => {
  const { user } = useAuth();
  const { selectedCurrency } = useCurrency();
  const { planFeatures, loading: subscriptionLoading } = useSubscription();
  const { joinConversation, leaveConversation, sendTypingIndicator, on, off } = useSocket();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConv, setSelectedConv] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatMessage, setChatMessage] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [otherUserProfile, setOtherUserProfile] = useState(null);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [showChatMenu, setShowChatMenu] = useState(false);

  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const prevConvIdRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const chatMenuRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const isUserScrolledUpRef = useRef(false);

  const currentUserId = user?.id;
  const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
  const canChat = isAdminUser || planFeatures?.canLiveChat;
  const isBasicPlan = !canChat && !subscriptionLoading;

  // ============ Redirect to mobile messenger on mobile devices ============
  useEffect(() => {
    if (window.innerWidth <= 768) {
      navigate('/mobile/messenger', { replace: true });
    }
  }, [navigate]);

  // ============ Fetch conversations ============
  const fetchConversations = useCallback(async (silent = false) => {
    if (!currentUserId) return;
    try {
      if (!silent) setLoading(true);
      const data = await messageService.getConversations(currentUserId);
      setConversations(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);

  // Auto-select conversation from URL param ?chatId=<id>
  useEffect(() => {
    const chatId = searchParams.get('chatId');
    if (!chatId || conversations.length === 0 || selectedConv) return;
    const targetId = parseInt(chatId, 10);
    const match = conversations.find(c => c.id === targetId);
    if (match) {
      handleSelectConversation(match);
      // Remove param from URL without re-triggering
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, searchParams]);

  // Polling fallback every 8s
  useEffect(() => {
    if (!currentUserId) return;
    const interval = setInterval(() => fetchConversations(true), 8000);
    return () => clearInterval(interval);
  }, [currentUserId, fetchConversations]);

  // ============ Socket event handlers ============
  useEffect(() => {
    if (!on || !off) return;

    const handleNewMessage = ({ conversationId, message }) => {
      if (selectedConv && selectedConv.id === conversationId) {
        setChatMessages(prev => {
          if (prev.find(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
        // Mark as read since user is viewing
        messageService.markReadStatus(conversationId).catch(() => {});
      }
      fetchConversations(true);
    };

    const handleConvUpdated = () => {
      fetchConversations(true);
    };

    const handleTyping = ({ userId, conversationId, isTyping }) => {
      if (userId !== currentUserId) {
        setTypingUsers(prev => ({
          ...prev,
          [conversationId]: isTyping ? userId : null
        }));
        if (isTyping) {
          setTimeout(() => {
            setTypingUsers(prev => ({ ...prev, [conversationId]: null }));
          }, 4000);
        }
      }
    };

    const handleMessagesRead = ({ conversationId }) => {
      if (selectedConv && selectedConv.id === conversationId) {
        setChatMessages(prev => prev.map(msg =>
          msg.isOwn && msg.status !== 'read' ? { ...msg, status: 'read' } : msg
        ));
      }
    };

    const handleMessagesDelivered = ({ conversationId }) => {
      if (selectedConv && selectedConv.id === conversationId) {
        setChatMessages(prev => prev.map(msg =>
          msg.isOwn && msg.status === 'sent' ? { ...msg, status: 'delivered' } : msg
        ));
      }
    };

    on('new_message', handleNewMessage);
    on('conversation_updated', handleConvUpdated);
    on('new_conversation', handleConvUpdated);
    on('user_typing', handleTyping);
    on('messages_read', handleMessagesRead);
    on('messages_delivered', handleMessagesDelivered);

    return () => {
      off('new_message', handleNewMessage);
      off('conversation_updated', handleConvUpdated);
      off('new_conversation', handleConvUpdated);
      off('user_typing', handleTyping);
      off('messages_read', handleMessagesRead);
      off('messages_delivered', handleMessagesDelivered);
    };
  }, [on, off, selectedConv, currentUserId, fetchConversations]);

  // ============ Join/leave conversation rooms ============
  useEffect(() => {
    if (prevConvIdRef.current && prevConvIdRef.current !== selectedConv?.id) {
      leaveConversation(prevConvIdRef.current);
    }
    if (selectedConv?.id) {
      joinConversation(selectedConv.id);
      prevConvIdRef.current = selectedConv.id;
    }
    return () => {
      if (prevConvIdRef.current) {
        leaveConversation(prevConvIdRef.current);
      }
    };
  }, [selectedConv?.id, joinConversation, leaveConversation]);

  // Track if user has scrolled up in the chat container
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      isUserScrolledUpRef.current = scrollHeight - scrollTop - clientHeight > 80;
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [selectedConv?.id]);

  // Smart auto scroll
  useEffect(() => {
    if (!chatMessages || chatMessages.length === 0) {
      prevMessageCountRef.current = 0;
      return;
    }
    const prevCount = prevMessageCountRef.current;
    prevMessageCountRef.current = chatMessages.length;

    // On chat switch (count went from 0 to N), scroll instantly to bottom
    if (prevCount === 0 && chatMessages.length > 0) {
      requestAnimationFrame(() => {
        const container = chatContainerRef.current;
        if (container) container.scrollTop = container.scrollHeight;
      });
      isUserScrolledUpRef.current = false;
      return;
    }
    // New message arrived — scroll smoothly only if user is near bottom
    if (chatMessages.length > prevCount && !isUserScrolledUpRef.current) {
      requestAnimationFrame(() => {
        const container = chatContainerRef.current;
        if (container) {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      });
    }
  }, [chatMessages]);

  // Close chat menu on outside click
  useEffect(() => {
    if (!showChatMenu) return;
    const handleClickOutside = (e) => {
      if (chatMenuRef.current && !chatMenuRef.current.contains(e.target)) {
        setShowChatMenu(false);
      }
    };
    // Use setTimeout so the listener is added after current click event finishes
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showChatMenu]);

  // ============ Search ============
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await messageService.searchMessages(searchQuery.trim());
        setSearchResults(results);
      } catch { setSearchResults([]); }
      finally { setIsSearching(false); }
    }, 400);
  }, [searchQuery]);

  // ============ Select conversation ============
  const handleSelectConversation = async (conv) => {
    setSelectedConv(conv);
    setShowMobileChat(true);
    setShowSearch(false);
    setSearchQuery('');
    try {
      const data = await messageService.getMessages(conv.id, currentUserId);
      if (data?.messages) {
        setChatMessages(data.messages);
        if (data.sender) {
          setOtherUserProfile(data.sender);
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // ============ Send message ============
  const handleSendChat = async () => {
    if (!chatMessage.trim() || !selectedConv || isBasicPlan) return;
    setSendingChat(true);
    try {
      const sentMsg = await messageService.sendMessage(selectedConv.id, currentUserId, chatMessage.trim());
      if (sentMsg) {
        setChatMessages(prev => [...prev, { ...sentMsg, isOwn: true }]);
      }
      setChatMessage('');
      fetchConversations(true);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSendingChat(false);
    }
  };

  // ============ Typing indicator ============
  const handleTypingInput = () => {
    if (!selectedConv) return;
    sendTypingIndicator(selectedConv.id, true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(selectedConv.id, false);
    }, 2000);
  };

  // ============ File attachment ============
  const handleFileSelect = async (e, type) => {
    const file = e.target.files[0];
    if (!file || !selectedConv || isBasicPlan) return;
    setShowAttachMenu(false);

    if (type === 'image' && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview({ file, url: ev.target.result, name: file.name });
      reader.readAsDataURL(file);
      return;
    }

    setUploadingFile(true);
    try {
      const sentMsg = await messageService.uploadAttachment(selectedConv.id, currentUserId, file);
      if (sentMsg) {
        setChatMessages(prev => [...prev, { ...sentMsg, isOwn: true }]);
      }
      fetchConversations(true);
    } catch (error) {
      console.error('File upload error:', error);
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleSendImage = async () => {
    if (!imagePreview || !selectedConv) return;
    setUploadingFile(true);
    try {
      const sentMsg = await messageService.uploadAttachment(
        selectedConv.id, currentUserId, imagePreview.file, chatMessage.trim()
      );
      if (sentMsg) {
        setChatMessages(prev => [...prev, { ...sentMsg, isOwn: true }]);
      }
      setImagePreview(null);
      setChatMessage('');
      fetchConversations(true);
    } catch (error) {
      console.error('Image upload error:', error);
    } finally {
      setUploadingFile(false);
    }
  };

  // ============ Delete conversation ============
  const handleDeleteConversation = async (e, convId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await messageService.deleteConversation(convId, currentUserId);
      if (selectedConv?.id === convId) {
        setSelectedConv(null);
        setChatMessages([]);
      }
      fetchConversations(true);
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  // ============ Helpers ============
  const getAvatarUrl = (avatar) => {
    if (!avatar) return null;
    if (avatar.startsWith('http')) return avatar;
    if (avatar.startsWith('/uploads')) return avatar;
    return `${API_BASE_URL}${avatar}`;
  };

  const getAttachmentUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return url;
    return `${API_BASE_URL}${url}`;
  };

  const formatMsgTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDateSeparator = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const shouldShowDateSeparator = (msgs, index) => {
    if (index === 0) return true;
    return new Date(msgs[index].createdAt).toDateString() !== new Date(msgs[index - 1].createdAt).toDateString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleProfileClick = () => {
    if (otherUserProfile?.sellerSlug) {
      navigate(`/supplier/${otherUserProfile.sellerSlug}`);
    } else if (otherUserProfile?.sellerId) {
      navigate(`/seller/${otherUserProfile.sellerId}`);
    } else if (selectedConv?.sender?.sellerSlug) {
      navigate(`/supplier/${selectedConv.sender.sellerSlug}`);
    } else if (selectedConv?.sender?.sellerId) {
      navigate(`/seller/${selectedConv.sender.sellerId}`);
    }
  };

  const hasProfile = otherUserProfile?.sellerSlug || otherUserProfile?.sellerId || selectedConv?.sender?.sellerSlug || selectedConv?.sender?.sellerId;

  const handleCallClick = () => {
    setShowChatMenu(false);
    const phone = otherUserProfile?.phone || selectedConv?.sender?.phone;
    if (phone) {
      const cleaned = phone.replace(/[\s\-()]/g, '');
      window.open(`tel:${cleaned}`, '_self');
    } else {
      alert('No phone number available for this user.');
    }
  };

  const handleDeleteFromHeader = async () => {
    if (!selectedConv) return;
    const convId = selectedConv.id;
    setShowChatMenu(false);
    if (!window.confirm('Are you sure you want to delete this conversation? This cannot be undone.')) return;
    try {
      await messageService.deleteConversation(convId, currentUserId);
      setSelectedConv(null);
      setChatMessages([]);
      setOtherUserProfile(null);
      setShowMobileChat(false);
      fetchConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Failed to delete conversation');
    }
  };

  const renderStatusTicks = (status) => {
    if (!status) return null;
    if (status === 'sent') return <span className="wa-tick wa-tick-sent">&#10003;</span>;
    if (status === 'delivered') return <span className="wa-tick wa-tick-delivered">&#10003;&#10003;</span>;
    if (status === 'read') return <span className="wa-tick wa-tick-read">&#10003;&#10003;</span>;
    return null;
  };

  return (
    <SellerDashboardLayout>
      <div className="wa-messenger">
        {/* ====== LEFT: INBOX PANEL ====== */}
        <div className={`wa-inbox-panel ${showMobileChat ? 'wa-hide-mobile' : ''}`}>
          <div className="wa-inbox-header">
            <h2>Chats</h2>
            <div className="wa-inbox-header-actions">
              <button
                className={`wa-icon-btn ${showSearch ? 'active' : ''}`}
                onClick={() => { setShowSearch(!showSearch); setSearchQuery(''); setSearchResults([]); }}
                title="Search messages"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </button>
            </div>
          </div>

          {showSearch && (
            <div className="wa-search-bar">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button className="wa-search-clear" onClick={() => { setSearchQuery(''); setSearchResults([]); }}>&#10005;</button>
              )}
            </div>
          )}

          {showSearch && searchQuery.length >= 2 ? (
            <div className="wa-search-results">
              {isSearching ? (
                <div className="wa-inbox-empty">Searching...</div>
              ) : searchResults.length === 0 ? (
                <div className="wa-inbox-empty">No messages found</div>
              ) : (
                searchResults.map((result, idx) => (
                  <div
                    key={idx}
                    className="wa-search-result-item"
                    onClick={() => {
                      const conv = conversations.find(c => c.id === result.conversationId);
                      if (conv) handleSelectConversation(conv);
                    }}
                  >
                    <div className="wa-search-result-name">{result.otherUser?.name || 'User'}</div>
                    <div className="wa-search-result-text">{result.content}</div>
                    <div className="wa-search-result-time">{result.time}</div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="wa-inbox-list">
              {loading ? (
                <div className="wa-inbox-empty">Loading...</div>
              ) : conversations.length === 0 ? (
                <div className="wa-inbox-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <p>No conversations yet</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.id}
                    className={`wa-inbox-item${selectedConv?.id === conv.id ? ' wa-inbox-item-active' : ''}${!conv.isRead ? ' wa-inbox-item-unread' : ''}`}
                    onClick={() => handleSelectConversation(conv)}
                  >
                    <div className="wa-inbox-avatar">
                      {conv.sender?.avatar ? (
                        <img src={getAvatarUrl(conv.sender.avatar)} alt="" />
                      ) : (
                        <span>{conv.sender?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                      )}
                    </div>
                    <div className="wa-inbox-info">
                      <div className="wa-inbox-top-row">
                        <span className="wa-inbox-name">{conv.sender?.name || 'Unknown'}</span>
                        <span className="wa-inbox-time">{conv.time || ''}</span>
                      </div>
                      <div className="wa-inbox-bottom-row">
                        <span className={`wa-inbox-preview${!conv.isRead ? ' wa-preview-unread' : ''}`}>
                          {typingUsers[conv.id] ? (
                            <em className="wa-typing-text">typing...</em>
                          ) : (
                            conv.preview || 'No messages yet'
                          )}
                        </span>
                        {!conv.isRead && <span className="wa-unread-badge" />}
                      </div>
                    </div>
                    <button
                      className="wa-inbox-delete-btn"
                      onClick={(e) => handleDeleteConversation(e, conv.id)}
                      title="Delete"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* ====== RIGHT: CHAT PANEL ====== */}
        <div className={`wa-chat-panel ${showMobileChat ? 'wa-show-mobile' : ''}`}>
          {!selectedConv ? (
            <div className="wa-chat-empty">
              <div className="wa-chat-empty-icon">
                <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h3>Gulf Constructor Messaging</h3>
              <p>Select a conversation to start messaging</p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="wa-chat-header">
                <button className="wa-back-btn wa-mobile-only" onClick={() => setShowMobileChat(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <div
                  className="wa-chat-header-avatar"
                  onClick={hasProfile ? handleProfileClick : undefined}
                  style={{ cursor: hasProfile ? 'pointer' : 'default' }}
                  title={hasProfile ? 'View Profile' : ''}
                >
                  {selectedConv.sender?.avatar ? (
                    <img src={getAvatarUrl(selectedConv.sender.avatar)} alt="" />
                  ) : (
                    <span>{selectedConv.sender?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                  )}
                </div>
                <div className="wa-chat-header-info" onClick={hasProfile ? handleProfileClick : undefined} style={{ cursor: hasProfile ? 'pointer' : 'default' }}>
                  <span className="wa-chat-header-name">{selectedConv.sender?.name || 'Unknown'}</span>
                  <span className="wa-chat-header-status">
                    {typingUsers[selectedConv.id] ? (
                      <em className="wa-typing-text">typing...</em>
                    ) : (
                      selectedConv.sender?.role || 'User'
                    )}
                  </span>
                </div>
                <div className="wa-chat-header-actions">
                  <button className="wa-icon-btn" onClick={handleCallClick} title="Call">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                  </button>
                  {hasProfile && (
                    <button className="wa-icon-btn" onClick={handleProfileClick} title="View Profile">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                    </button>
                  )}
                  <div className="wa-chat-menu-container" ref={chatMenuRef}>
                    <button className="wa-icon-btn" onClick={() => setShowChatMenu(!showChatMenu)} title="More options">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="5" r="1" fill="currentColor"/>
                        <circle cx="12" cy="12" r="1" fill="currentColor"/>
                        <circle cx="12" cy="19" r="1" fill="currentColor"/>
                      </svg>
                    </button>
                    {showChatMenu && (
                      <div className="wa-chat-dropdown-menu">
                        {hasProfile && (
                          <button onClick={() => { handleProfileClick(); setShowChatMenu(false); }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                            </svg>
                            View Profile
                          </button>
                        )}
                        <button onClick={handleCallClick}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                          </svg>
                          Call
                        </button>
                        <button className="wa-menu-delete" onClick={handleDeleteFromHeader}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                          </svg>
                          Delete Chat
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Image Preview Overlay */}
              {imagePreview && (
                <div className="wa-image-preview-overlay">
                  <div className="wa-image-preview-header">
                    <button className="wa-icon-btn" onClick={() => { setImagePreview(null); if (imageInputRef.current) imageInputRef.current.value = ''; }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                    <span>{imagePreview.name}</span>
                  </div>
                  <div className="wa-image-preview-body">
                    <img src={imagePreview.url} alt="Preview" />
                  </div>
                  <div className="wa-image-preview-footer">
                    <input
                      type="text"
                      placeholder="Add a caption..."
                      value={chatMessage}
                      onChange={(e) => setChatMessage(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSendImage(); }}
                    />
                    <button className="wa-send-btn" onClick={handleSendImage} disabled={uploadingFile}>
                      {uploadingFile ? <div className="wa-spinner" /> : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                          <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Messages Area */}
              <div className="wa-chat-messages" ref={chatContainerRef}>
                {chatMessages.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#94a3b8', padding: 32, width: '100%', margin: 'auto' }}>No messages yet</div>
                ) : chatMessages.map((msg, idx) => {
                  const showDate = shouldShowDateSeparator(chatMessages, idx);
                  let rfqData = null;
                  if (msg.type === 'rfq' && msg.products) {
                    rfqData = Array.isArray(msg.products) ? msg.products[0] : msg.products;
                  }

                  return (
                    <React.Fragment key={msg.id || idx}>
                      {showDate && (
                        <div className="wa-date-separator">
                          <span>{formatDateSeparator(msg.createdAt)}</span>
                        </div>
                      )}

                      {msg.type === 'rfq' && rfqData ? (
                        <div className={`wa-msg ${msg.isOwn ? 'wa-msg-out' : 'wa-msg-in'}`}>
                          <div className={`wa-msg-bubble ${msg.isOwn ? 'wa-bubble-out' : 'wa-bubble-in'}`}>
                            <div className="wa-rfq-card">
                              <div className="wa-rfq-header">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                  <polyline points="14 2 14 8 20 8"/>
                                </svg>
                                <span>RFQ #{rfqData.rfqId || 'N/A'}</span>
                              </div>
                              <div className="wa-rfq-body">
                                <div className="wa-rfq-row"><span>Category:</span><span>{rfqData.category}{rfqData.subCategory ? ` > ${rfqData.subCategory}` : ''}</span></div>
                                {rfqData.quantity && <div className="wa-rfq-row"><span>Qty:</span><span>{rfqData.quantity} {rfqData.unit || 'Units'}</span></div>}
                                {rfqData.offerPrice && <div className="wa-rfq-row"><span>Budget:</span><span>{rfqData.currency || selectedCurrency.code} {rfqData.offerPrice}</span></div>}
                                {rfqData.details && <p className="wa-rfq-details">{rfqData.details}</p>}
                              </div>
                            </div>
                            <div className="wa-msg-meta">
                              <span className="wa-msg-time">{formatMsgTime(msg.createdAt)}</span>
                              {msg.isOwn && renderStatusTicks(msg.status)}
                            </div>
                          </div>
                        </div>
                      ) : msg.type === 'image' && msg.attachmentUrl ? (
                        <div className={`wa-msg ${msg.isOwn ? 'wa-msg-out' : 'wa-msg-in'}`}>
                          <div className={`wa-msg-bubble wa-msg-image-bubble ${msg.isOwn ? 'wa-bubble-out' : 'wa-bubble-in'}`}>
                            <img
                              src={getAttachmentUrl(msg.attachmentUrl)}
                              alt="Shared"
                              className="wa-chat-image"
                              onClick={() => window.open(getAttachmentUrl(msg.attachmentUrl), '_blank')}
                            />
                            {msg.content && msg.content !== msg.attachmentName && (
                              <div className="wa-image-caption">{msg.content}</div>
                            )}
                            <div className="wa-msg-meta wa-img-meta">
                              <span className="wa-msg-time">{formatMsgTime(msg.createdAt)}</span>
                              {msg.isOwn && renderStatusTicks(msg.status)}
                            </div>
                          </div>
                        </div>
                      ) : msg.type === 'file' && msg.attachmentUrl ? (
                        <div className={`wa-msg ${msg.isOwn ? 'wa-msg-out' : 'wa-msg-in'}`}>
                          <div className={`wa-msg-bubble ${msg.isOwn ? 'wa-bubble-out' : 'wa-bubble-in'}`}>
                            <a
                              href={getAttachmentUrl(msg.attachmentUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="wa-file-attachment"
                            >
                              <div className="wa-file-icon">
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                  <polyline points="14 2 14 8 20 8"/>
                                </svg>
                              </div>
                              <div className="wa-file-info">
                                <span className="wa-file-name">{msg.attachmentName || 'File'}</span>
                                <span className="wa-file-size">{formatFileSize(msg.attachmentSize)}</span>
                              </div>
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                              </svg>
                            </a>
                            {msg.content && msg.content !== msg.attachmentName && (
                              <div className="wa-file-caption">{msg.content}</div>
                            )}
                            <div className="wa-msg-meta">
                              <span className="wa-msg-time">{formatMsgTime(msg.createdAt)}</span>
                              {msg.isOwn && renderStatusTicks(msg.status)}
                            </div>
                          </div>
                        </div>
                      ) : msg.type === 'inquiry' && msg.products ? (
                        <div className={`wa-msg ${msg.isOwn ? 'wa-msg-out' : 'wa-msg-in'}`}>
                          <div className={`wa-msg-bubble ${msg.isOwn ? 'wa-bubble-out' : 'wa-bubble-in'}`}>
                            <div className="wa-inquiry-card">
                              <strong>Product Inquiry</strong>
                              {Array.isArray(msg.products) && msg.products.map((p, i) => (
                                <div key={i} className="wa-inquiry-item">
                                  {p.image && <img src={p.image.startsWith('http') ? p.image : `${API_BASE_URL}${p.image}`} alt="" />}
                                  <div>
                                    <div className="wa-inquiry-name">{p.name || p.description}</div>
                                    {p.price && <div className="wa-inquiry-price">{p.price}</div>}
                                  </div>
                                </div>
                              ))}
                            </div>
                            {msg.content && <div style={{ marginTop: 6 }}>{msg.content}</div>}
                            <div className="wa-msg-meta">
                              <span className="wa-msg-time">{formatMsgTime(msg.createdAt)}</span>
                              {msg.isOwn && renderStatusTicks(msg.status)}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className={`wa-msg ${msg.isOwn ? 'wa-msg-out' : 'wa-msg-in'}`}>
                          <div className={`wa-msg-bubble ${msg.isOwn ? 'wa-bubble-out' : 'wa-bubble-in'}`}>
                            <div className="wa-msg-text">{msg.content}</div>
                            <div className="wa-msg-meta">
                              <span className="wa-msg-time">{formatMsgTime(msg.createdAt)}</span>
                              {msg.isOwn && renderStatusTicks(msg.status)}
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}

                {typingUsers[selectedConv?.id] && (
                  <div className="wa-msg wa-msg-in">
                    <div className="wa-msg-bubble wa-bubble-in wa-typing-bubble">
                      <div className="wa-typing-dots">
                        <span /><span /><span />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Input Bar */}
              {isBasicPlan ? (
                <div className="wa-upgrade-bar">
                  <strong>Basic Plan</strong> &mdash; You can view but not send messages.{' '}
                  <a href="/membership-packages">Upgrade your plan</a>
                </div>
              ) : (
                <div className="wa-input-bar">
                  <div className="wa-attach-container">
                    <button className="wa-icon-btn" onClick={() => setShowAttachMenu(!showAttachMenu)} title="Attach">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                      </svg>
                    </button>
                    {showAttachMenu && (
                      <div className="wa-attach-menu">
                        <button onClick={() => { imageInputRef.current?.click(); setShowAttachMenu(false); }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                          </svg>
                          Image
                        </button>
                        <button onClick={() => { fileInputRef.current?.click(); setShowAttachMenu(false); }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                          Document
                        </button>
                      </div>
                    )}
                    <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => handleFileSelect(e, 'image')} />
                    <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip" style={{ display: 'none' }} onChange={(e) => handleFileSelect(e, 'file')} />
                  </div>

                  <input
                    type="text"
                    className="wa-chat-input"
                    placeholder="Type a message"
                    value={chatMessage}
                    onChange={(e) => { setChatMessage(e.target.value); handleTypingInput(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                    disabled={uploadingFile}
                  />

                  <button className="wa-send-btn" onClick={handleSendChat} disabled={sendingChat || !chatMessage.trim() || uploadingFile}>
                    {uploadingFile ? <div className="wa-spinner" /> : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </SellerDashboardLayout>
  );
};

export default MessagePage;
